import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ProductService } from '../../../../../../../services/AppDataServices/services/product.service';
import { CategoryService } from '../../../../../../../services/AppDataServices/services/category.service';
import { useLanguage } from '../../../../../../../hooks/useLanguage';
import { pos as frPos } from '../../../../../../../locales/fr/pos';
import { pos as esPos } from '../../../../../../../locales/es/pos';
import { pos as arPos } from '../../../../../../../locales/ar/pos';

const translations = {
  fr: frPos,
  es: esPos,
  ar: arPos
};

const ProductGrid = ({ 
  onProductClick, 
  commandes, 
  currentCommandeIndex, 
  createNewCommande, 
  switchToCommande 
}) => {
  const { lang } = useLanguage();
  const t = translations[lang] || translations.fr;
  
  const [selectedCategory, setSelectedCategory] = useState(t.productGrid.categories.all);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryStartIndex, setCategoryStartIndex] = useState(0);

  // Instancier les services une seule fois avec useMemo
  const productService = useMemo(() => new ProductService(), []);
  const categoryService = useMemo(() => new CategoryService(), []);

  // Charger les produits
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Récupérer tous les produits puis filtrer côté client
      const allProducts = await productService.getAll();
      
      // Vérifier que les données sont valides
      if (!Array.isArray(allProducts)) {
        throw new Error('Format de données invalide');
      }
      
      // Filtrer les produits actifs côté client
      const activeProducts = allProducts.filter(product => 
        product && 
        product.isActive !== false && 
        product.name // S'assurer que le produit a un nom
      );
      
      // Formater les produits pour l'affichage
      const formattedProducts = activeProducts.map(product => ({
        id: product.id,
        name: product.name || t.productGrid.noProducts,
        price: parseFloat(product.price) || 0,
        category: product.categoryName || t.productGrid.categories.all,
        image: product.image || null, // null si pas d'image
        stock: parseInt(product.stock) || 0,
        barcode: product.barcode || '',
        categoryId: product.categoryId
      }));
      
      setProducts(formattedProducts);
    } catch (err) {
      console.error('Erreur lors du chargement des produits:', err);
      setError(`${t.productGrid.error}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []); // Supprimer les dépendances

  // Charger les catégories
  const loadCategories = useCallback(async () => {
    try {
      const allCategories = await categoryService.getAll();
      // Filtrer les catégories actives côté client
      const activeCategories = allCategories.filter(category => category.isActive !== false);
      setCategories(activeCategories);
    } catch (err) {
      console.error('Erreur lors du chargement des catégories:', err);
      // Ne pas bloquer l'affichage si les catégories ne se chargent pas
      setCategories([]);
    }
  }, []); // Supprimer les dépendances

  // Effet pour charger les données au montage
  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []); // Charger seulement au montage

  // Effet pour masquer la scrollbar webkit
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      #category-container::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Effet pour mettre à jour la catégorie sélectionnée quand la langue change
  useEffect(() => {
    setSelectedCategory(t.productGrid.categories.all);
  }, [lang, t.productGrid.categories.all]);

  // Filtrer les produits par catégorie et recherche
  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === t.productGrid.categories.all || product.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.barcode?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  // Navigation des catégories
  const scrollCategories = (direction) => {
    const totalCategories = productCategories.length;
    const categoriesPerPage = 4;
    
    if (direction === 'left') {
      setCategoryStartIndex(Math.max(0, categoryStartIndex - categoriesPerPage));
    } else {
      setCategoryStartIndex(Math.min(
        totalCategories - categoriesPerPage, 
        categoryStartIndex + categoriesPerPage
      ));
    }
  };

  // Obtenir les catégories uniques des produits
  const productCategories = [t.productGrid.categories.all, ...new Set(products.map(p => p.category))];
  
  // Obtenir seulement 4 catégories à afficher
  const visibleCategories = productCategories.slice(categoryStartIndex, categoryStartIndex + 4);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-gray-500">{t.productGrid.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border h-full flex items-center justify-center">
        <div className="text-center text-red-500">
          <div className="text-4xl mb-2">❌</div>
          <p>{error}</p>
          <button 
            onClick={loadProducts}
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            {t.productGrid.retry}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border h-full flex flex-col">
      {/* En-tête avec recherche */}
      <div className="p-4 border-b bg-gray-50" aria-label={t.productGrid.header}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-800">{t.productGrid.title}</h3>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder={t.productGrid.search}
              value={searchTerm}
              onChange={handleSearchChange}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={t.productGrid.search}
            />
            <button 
              className="p-1 text-gray-500 hover:text-gray-700"
              aria-label={t.productGrid.search}
            >
              🔍
            </button>
          </div>
        </div>
        
        {/* Navigation des commandes */}
        <div className="flex items-center gap-2" aria-label={t.productGrid.orders}>
          <span className="text-sm font-medium text-gray-700">{t.productGrid.orders}:</span>
          <div className="flex items-center gap-1">
            {commandes.map((commande, index) => (
              <button
                key={index}
                onClick={() => switchToCommande(index)}
                className={`w-8 h-8 rounded-full text-sm font-medium transition-all duration-200 ${
                  index === currentCommandeIndex
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:shadow-sm'
                }`}
                title={`${t.productGrid.order} ${index + 1}${commande.items.length > 0 ? ` (${commande.items.length} ${t.productGrid.articles})` : ''}`}
                aria-label={`${t.productGrid.order} ${index + 1}${commande.items.length > 0 ? ` (${commande.items.length} ${t.productGrid.articles})` : ''}`}
              >
                {index + 1}
              </button>
            ))}
            <button
              onClick={createNewCommande}
              className="w-8 h-8 rounded-full bg-green-500 text-white text-lg font-bold hover:bg-green-600 hover:shadow-md transition-all duration-200 flex items-center justify-center"
              title={t.productGrid.newOrder}
              aria-label={t.productGrid.newOrder}
            >
              +
            </button>
          </div>
        </div>
      </div>
      
      {/* Grille de produits */}
      <div className="flex-1 p-4 overflow-y-auto" aria-label={t.productGrid.productGrid}>
        {filteredProducts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">📦</div>
            <p>
              {searchTerm || selectedCategory !== t.productGrid.categories.all 
                ? t.productGrid.noProductsWithCriteria
                : t.productGrid.noProductsAvailable
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-3">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                onClick={() => onProductClick(product)}
                className="bg-gray-50 rounded-lg p-3 cursor-pointer hover:bg-blue-50 transition-colors border border-gray-200 hover:border-blue-300"
                role="button"
                tabIndex={0}
                aria-label={`${product.name} - ${product.price.toFixed(2)} - ${t.productGrid.stock}: ${product.stock}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onProductClick(product);
                  }
                }}
              >
                <div className="text-center">
                  <div className="text-3xl mb-2">
                    {product.image ? (
                      product.image.startsWith('http') || product.image.startsWith('data:') ? (
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded-lg mx-auto"
                        />
                      ) : (
                        <span>{product.image}</span>
                      )
                    ) : (
                      <svg 
                        className="w-12 h-12 text-gray-400 mx-auto" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={1.5} 
                          d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" 
                        />
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={1.5} 
                          d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" 
                        />
                      </svg>
                    )}
                  </div>
                  <h4 className="font-medium text-sm text-gray-800 mb-1 truncate">
                    {product.name}
                  </h4>
                  <p className="text-xs text-gray-500 mb-2">{product.barcode || t.productGrid.noBarcode}</p>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-lg font-bold text-blue-600">
                      {product.price.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t.productGrid.stock}: {product.stock}
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1">
                    <div 
                      className="bg-blue-500 h-1 rounded-full transition-all"
                      style={{ 
                        width: `${Math.min((product.stock / 50) * 100, 100)}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Filtres par catégorie avec navigation - EN BAS */}
      <div className="p-4 border-t bg-gray-50">
        <div className="relative">
          {/* Bouton flèche gauche */}
          {categoryStartIndex > 0 && (
            <button
              onClick={() => scrollCategories('left')}
              className="absolute left-1 top-1/2 transform -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-2 shadow-md hover:bg-gray-50 hover:shadow-lg transition-all duration-200"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          
          {/* Bouton flèche droite */}
          {categoryStartIndex + 4 < productCategories.length && (
            <button
              onClick={() => scrollCategories('right')}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-2 shadow-md hover:bg-gray-50 hover:shadow-lg transition-all duration-200"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          
          {/* Container des catégories avec scroll */}
          <div 
            id="category-container"
            className="flex gap-3 overflow-hidden pb-2 px-12"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              WebkitScrollbar: { display: 'none' }
            }}
          >
            {visibleCategories.map((category, index) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`px-4 py-2 text-sm rounded-lg whitespace-nowrap transition-all duration-200 flex-1 min-w-0 font-medium ${
                  selectedCategory === category
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm border border-gray-200'
                }`}
              >
                <span className="truncate block">{category}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductGrid; 